/**
 * Safety rate vocabulary and presentation — the frontend half of the API's
 * `safety-metrics.service.ts`.
 *
 * ── THE ONE RULE ─────────────────────────────────────────────────────────────
 * A rate is null, or it is true. There is no `|| 0` in this file and there must
 * never be one. The API was rewritten specifically because the old TRIR
 * returned 0 on an empty database, so a company with a real safety problem and
 * a company that had never entered an hour both rendered "TRIR 0" — on a figure
 * that goes on prequalification packages. Reintroducing the coercion here would
 * restore the bug one layer up.
 *
 * ── THREE STATES, NOT TWO ────────────────────────────────────────────────────
 * The distinction this module exists to preserve:
 *
 *   value null,  reason 'no_exposure_data'  → no month in the window has hours
 *                                            from any source (clock-in, tickets
 *                                            or an office edit)
 *   value null,  reason 'zero_denominator'  → months are covered and sum to 0
 *   value 0,     reason null                → real hours, nobody hurt. A RESULT.
 *
 * The third is an achievement and renders as "0.00" at full strength. The first
 * two render as an em dash with the reason spelled out. Collapsing any pair of
 * them is the defect.
 *
 * NOTHING HERE COMPUTES A RATE. `value` is displayed as the API sent it. If a
 * number looks wrong, the fix is in safety-metrics.service.ts — two answers to
 * one question is worse than one wrong answer.
 */

import { fmtHours, fmtQty, fmtRate } from './display-labels';
import { calendarDayString, formatCalendarDate } from './calendar-date';

/** The stored day as 'YYYY-MM-DD'; an unreadable value passes through as it came, absent as ''. */
const calendarDayOrRaw = (v: unknown): string => {
  const s = v == null ? '' : String(v);
  return calendarDayString(s) ?? s;
};

/** Reason codes the API returns alongside a null rate. Exhaustive — see RATE_REASONS. */
export type RateReason = 'no_exposure_data' | 'zero_denominator';

/**
 * A single rate as the API sends it.
 *
 * `denominator` is nullable and that nullability is load-bearing: `null` means
 * no exposure row existed at all, `0` means one existed and held zero. Do not
 * narrow it to `number`.
 *
 * Note there is no `monthsCovered` here — the month count is reported once, on
 * `exposure`, because it describes the window rather than any one rate.
 */
export interface Rate {
  value: number | null;
  numerator: number;
  denominator: number | null;
  base: number;
  reason: RateReason | null;
}

export interface MetricSettings {
  trifBase: number;
  mvirBase: number;
  mviCostThreshold: number;
  fiscalYearStartMonth: number;
  exposureScope: string;
}

export interface SafetyMetrics {
  window: MetricWindow;
  periodStart: string;
  periodEnd: string;
  timezone: string;
  settings: MetricSettings;
  exposure: {
    hoursWorked: number | null;
    kmDriven: number | null;
    /** Months with hours from ANY source — auto (clock-in / tickets) or edited. */
    monthsCovered: number;
    monthsInWindow: number;
    monthsFromClockIn: number;
    monthsEdited: number;
    employeeCountLatest: number | null;
    months: ExposureMonth[];
  };
  counts: {
    totalIncidents: number;
    recordable: number;
    lostTime: number;
    dart: number;
    nearMiss: number;
    motorVehiclePreventable: number;
    unclassified: number;
    daysAway: number;
    daysRestricted: number;
  };
  rates: Record<MetricKey, Rate>;
}

export type MetricWindow = 'rolling12' | 'ytd';

export const METRIC_WINDOW_LABELS: Record<MetricWindow, string> = {
  rolling12: 'Rolling 12 months',
  ytd: 'Year to date',
};

/** The six rates, in display order. Keys match `rates` on the API response. */
export type MetricKey = 'trif' | 'ltif' | 'dart' | 'severity' | 'nearMissFrequency' | 'mvir';

/** Which exposure figure a rate divides by. Drives the reason copy. */
type Denominator = 'hours' | 'km';

export interface MetricDef {
  key: MetricKey;
  label: string;
  /** Expanded on the tile so nobody has to remember what the acronym stands for. */
  description: string;
  denominator: Denominator;
  /** Noun for the numerator, e.g. "3 recordables". */
  numeratorNoun: { one: string; many: string };
  /**
   * Tile tone.
   *
   * Five of the six are `neutral` on purpose. A rate is a measurement, not a
   * grade, and the app has no idea what the customer's threshold is — colouring
   * a TRIF red would be inventing a verdict, and colouring a genuine 0.00 green
   * would make a real result indistinguishable from a congratulation.
   *
   * Near-miss frequency is the exception and it is `purple`, matching the near
   * miss tile already on Safety & Compliance. It is NEVER red or amber: a
   * rising near-miss count means crews are reporting, which is the outcome the
   * programme wants. Styling it as a hazard teaches people to stop reporting,
   * and then every other number on this page quietly gets worse.
   */
  tone: 'neutral' | 'purple';
}

export const METRIC_DEFS: MetricDef[] = [
  {
    key: 'trif',
    label: 'TRIF',
    description: 'Total recordable injury frequency',
    denominator: 'hours',
    numeratorNoun: { one: 'recordable', many: 'recordables' },
    tone: 'neutral',
  },
  {
    key: 'ltif',
    label: 'LTIF',
    description: 'Lost time injury frequency',
    denominator: 'hours',
    numeratorNoun: { one: 'lost-time injury', many: 'lost-time injuries' },
    tone: 'neutral',
  },
  {
    key: 'dart',
    label: 'DART',
    description: 'Days away, restricted, or transferred',
    denominator: 'hours',
    numeratorNoun: { one: 'DART case', many: 'DART cases' },
    tone: 'neutral',
  },
  {
    key: 'severity',
    label: 'Severity Rate',
    description: 'Days lost and restricted',
    denominator: 'hours',
    numeratorNoun: { one: 'day', many: 'days' },
    tone: 'neutral',
  },
  {
    key: 'nearMissFrequency',
    label: 'Near-Miss Frequency',
    description: 'Near misses reported — higher is better',
    denominator: 'hours',
    numeratorNoun: { one: 'near miss', many: 'near misses' },
    tone: 'purple',
  },
  {
    key: 'mvir',
    label: 'MVIR',
    description: 'Preventable motor vehicle incidents',
    denominator: 'km',
    numeratorNoun: { one: 'preventable incident', many: 'preventable incidents' },
    tone: 'neutral',
  },
];

/**
 * Reason copy, keyed on (denominator, reason).
 *
 * The API returns only two reason codes — there is no distinct
 * "no kilometres recorded" code — so the vehicle wording is derived from the
 * rate's denominator here rather than read off the payload. A `Record` of both
 * axes, not a fallback string: an unknown reason must fail the build, not
 * render as a blank line under an em dash.
 */
const REASON_COPY: Record<Denominator, Record<RateReason, string>> = {
  hours: {
    // Hours now arrive on their own from approved clock-in and tickets, so this
    // only fires when a whole window has neither and nobody edited a month.
    no_exposure_data: 'No clock-in or ticket hours in this window',
    zero_denominator: 'Recorded as zero',
  },
  km: {
    no_exposure_data: 'No kilometres recorded',
    zero_denominator: '0 km recorded',
  },
};

/** What a tile should draw. `kind` is the three-state distinction, made once. */
export type RateDisplay =
  | { kind: 'value'; text: string }
  | { kind: 'missing'; text: '—'; reason: string };

/**
 * Read a rate defensively and decide which of the three states it is in.
 *
 * Tolerates snake_case on every field. The API answers camelCase today, but
 * three separate bugs in one week were a frontend reading a key the payload did
 * not have and silently rendering a placeholder — and a placeholder is exactly
 * what this screen uses for "no data", so that failure would be invisible here.
 */
export function readRate(raw: any): Rate {
  return {
    value: raw?.value ?? null,
    numerator: raw?.numerator ?? 0,
    denominator: raw?.denominator ?? null,
    base: raw?.base ?? 0,
    reason: (raw?.reason ?? null) as RateReason | null,
  };
}

/**
 * The whole three-state decision, in one place.
 *
 * Order matters: a real 0 is checked by asking whether `value` is a finite
 * number, NOT by truthiness. `if (rate.value)` treats 0 as absent and is the
 * exact bug this page exists to fix.
 */
export function describeRate(rate: Rate, def: MetricDef): RateDisplay {
  if (typeof rate.value === 'number' && Number.isFinite(rate.value)) {
    return { kind: 'value', text: fmtRate(rate.value) };
  }

  const reason = rate.reason ?? 'no_exposure_data';
  return {
    kind: 'missing',
    text: '—',
    reason: REASON_COPY[def.denominator][reason]
      // A reason code the frontend has not been taught yet. Say so plainly
      // rather than drawing an unexplained gap or, worse, a zero.
      ?? 'Cannot be computed',
  };
}

const numberFmt = new Intl.NumberFormat('en-CA');

/** Pluralises against the numerator. "1 recordable", "0 recordables". */
export function numeratorPhrase(rate: Rate, def: MetricDef): string {
  const noun = rate.numerator === 1 ? def.numeratorNoun.one : def.numeratorNoun.many;
  return `${numberFmt.format(rate.numerator)} ${noun}`;
}

/**
 * The denominator half of the tile subtext, or null when there is nothing
 * honest to say. Absent renders nothing — never "0 hrs", which would read as a
 * measured zero.
 */
export function denominatorPhrase(rate: Rate, def: MetricDef): string | null {
  if (rate.denominator === null || !Number.isFinite(rate.denominator)) return null;
  const unit = def.denominator === 'km' ? 'km' : 'hrs';
  return `${numberFmt.format(rate.denominator)} ${unit}`;
}

/**
 * "1 recordable / 42,000 hrs · 12 months".
 *
 * `monthsCovered` comes from `exposure`, not from the rate — the API reports it
 * once for the window. It counts months with hours from any source, which is why it
 * can be lower than the 12 the window spans; that gap is the point of showing
 * it, because a TRIF built on 3 months of hours is not a 12-month TRIF.
 */
export function rateSubtext(rate: Rate, def: MetricDef, monthsCovered: number | null | undefined): string {
  const parts = [numeratorPhrase(rate, def)];
  const denom = denominatorPhrase(rate, def);
  if (denom) parts[0] = `${parts[0]} / ${denom}`;

  if (typeof monthsCovered === 'number' && Number.isFinite(monthsCovered)) {
    parts.push(`${monthsCovered} ${monthsCovered === 1 ? 'month' : 'months'}`);
  }
  return parts.join(' · ');
}

// ─── Exposure months (api#969) ──────────────────────────────────────────────
//
// One per month in the window, from GET /safety/exposure-months and from
// `exposure.months` on the metrics response. The API decides every figure:
// `effectiveHours` is the override when the office edited the month, else the
// auto figure from approved clock-in + tickets. Nothing here sums or picks.

export type ExposureMonthSource = 'clock_in' | 'edited';
export type ExposureAutoFrom = 'clock_in' | 'tickets' | 'tickets_and_clock_in';

export interface ExposureMonth {
  /** 'YYYY-MM-01'. */
  periodStart: string;
  /** null = no approved clock-in and no included ticket. Never a stand-in 0. */
  autoHours: number | null;
  autoReason: 'no_tickets_or_clock_in' | null;
  autoFrom: ExposureAutoFrom | null;
  overrideHours: number | null;
  /** What the rates use. */
  effectiveHours: number | null;
  /** null only when effectiveHours is null. */
  source: ExposureMonthSource | null;
  /** Manual-only. null when the month has no stored row. */
  kmDriven: number | null;
  employeeCount: number | null;
  rowId: string | null;
}

/** A finite number, or null. `Number(null)` is 0, so null is checked first. */
const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const SOURCES: readonly ExposureMonthSource[] = ['clock_in', 'edited'];
const AUTO_FROM: readonly ExposureAutoFrom[] = ['clock_in', 'tickets', 'tickets_and_clock_in'];

/** Reads either casing. An unrecognised source or autoFrom reads as null, never as a guess. */
export function readExposureMonth(raw: any): ExposureMonth {
  const source = raw?.source;
  const autoFrom = raw?.autoFrom ?? raw?.auto_from;
  return {
    periodStart: calendarDayOrRaw(raw?.periodStart ?? raw?.period_start),
    autoHours: numOrNull(raw?.autoHours ?? raw?.auto_hours),
    autoReason: (raw?.autoReason ?? raw?.auto_reason) === 'no_tickets_or_clock_in' ? 'no_tickets_or_clock_in' : null,
    autoFrom: AUTO_FROM.includes(autoFrom) ? autoFrom : null,
    overrideHours: numOrNull(raw?.overrideHours ?? raw?.override_hours),
    effectiveHours: numOrNull(raw?.effectiveHours ?? raw?.effective_hours),
    source: SOURCES.includes(source) ? source : null,
    kmDriven: numOrNull(raw?.kmDriven ?? raw?.km_driven),
    employeeCount: numOrNull(raw?.employeeCount ?? raw?.employee_count),
    rowId: (raw?.rowId ?? raw?.row_id) ?? null,
  };
}

/**
 * The tag beside a month's hours. Keyed on `source` AND `autoFrom`: the API's
 * own `sourceLabel` says "From clock-in" for every auto month, which would
 * mislabel a month built from tickets alone.
 */
export type ExposureTag = 'clock_in' | 'tickets' | 'tickets_and_clock_in' | 'edited';

export const EXPOSURE_TAG_LABELS: Record<ExposureTag, string> = {
  clock_in: 'From clock-in',
  tickets: 'From tickets',
  tickets_and_clock_in: 'From clock-in + tickets',
  edited: 'Edited',
};

/** null when the month has no hours, or an auto month whose basis the API did not name. */
export function exposureTag(m: ExposureMonth): ExposureTag | null {
  if (m.effectiveHours === null) return null;
  if (m.source === 'edited') return 'edited';
  if (m.source === 'clock_in') return m.autoFrom;
  return null;
}

/** Hours to two places ("163.15", a real zero "0.00"); absent is an em dash. */
export const fmtExposureHours = (v: number | null): string => fmtHours(v, 2);

/** km and headcount; absent is an em dash, a real zero is 0. */
export const fmtExposureCount = (v: number | null): string => fmtQty(v);

/**
 * "August 2026" from '2026-08-01'.
 *
 * Delegates to formatCalendarDate, which already constructs the date locally —
 * `new Date('2026-08-01')` is UTC midnight and renders as July west of
 * Greenwich, which would file a month of payroll hours under the wrong label on
 * every screen in Alberta. That fix lives in one place and this is not a second
 * one.
 */
export function formatPeriodMonth(periodStart: string): string {
  return formatCalendarDate(periodStart, { month: 'long', year: 'numeric' }, 'en-CA') ?? '—';
}

/** Current month as 'YYYY-MM' in LOCAL time — `toISOString()` would give UTC's month. */
export function currentMonthInputValue(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export const EXPOSURE_SCOPES = ['all_employee', 'field_only'] as const;
export type ExposureScope = typeof EXPOSURE_SCOPES[number];

/** Exhaustive — a scope with no label would render its raw code. */
export const EXPOSURE_SCOPE_LABELS: Record<ExposureScope, string> = {
  all_employee: 'All employees',
  field_only: 'Field only',
};

export const TRIF_BASES = [200_000, 1_000_000] as const;
export const MVIR_BASES = [1_000_000, 100_000] as const;

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
